import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { contasDisponiveis, guardarContaAtual, listarContas, sairDaContaAtual, sairDaContaGuardada, trocarPara } from './contasDoAparelho';
import { EVENTO_DA_FOLHA } from './eventoDaFolha';
import { MenuDaConta } from './MenuDaConta';
import { FRASE_DO_LIMITE, MAXIMO_DE_CONTAS, PAPEL, podeGuardar, type ContaGuardada } from './regrasDasContas';

/**
 * A FOLHA "TROCAR DE CONTA" (1.4, 23/10/2026; o jeito do Instagram e do Gmail, pedido do pesquisador). Montada UMA vez
 * (`App.tsx`) e aberta por `abrirFolhaDeContas()` — o item da gaveta e o botão de Minha conta.
 *
 * - A conta atual vem no topo, com ✓; as outras guardadas embaixo; "Adicionar conta" no fim. Tocar numa conta troca (com a
 *   biometria; prova guardada no aparelho bloqueia ANTES dela — `trocarPara`).
 * - Ninguém "guarda" à mão: a conta atual da equipe é guardada sozinha na primeira vez que a folha abre (o cofre não pede a
 *   biometria para guardar; a frase aparece enquanto guarda, para o aparelho que pedir). A conta que entra pelo "Adicionar
 *   conta" é guardada sozinha depois do login (`entrarComSessao`, em `useAuth`).
 * - Tirar uma conta: "⋯" → "Esquecer esta conta…" → a pergunta (`MenuDaConta`, 25/10/2026; antes, "Esquecer" ao lado da conta) —
 *   revoga no servidor. "Sair" não esquece: a conta continua na lista.
 * - Só no app e só para a equipe e as contas de teste (`podeGuardar`).
 */
const EVENTO = EVENTO_DA_FOLHA;
export const abrirFolhaDeContas = () => window.dispatchEvent(new Event(EVENTO));

export function FolhaDeContas() {
  const { user, entrarComSessao, sairParaAdicionarConta } = useAuth();
  const cliente = useQueryClient();
  const [aberta, setAberta] = useState(false);
  const [contas, setContas] = useState<ContaGuardada[]>([]);
  const [estado, setEstado] = useState<{ tom: 'info' | 'erro' | 'ok'; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState<number | 'guardar' | null>(null);

  const atual = user ? Number(user.id) : null;
  const recarregar = useCallback(async () => { const l = await listarContas(); setContas(l); return l; }, []);

  const abrir = useCallback(async () => {
    if (!user || !contasDisponiveis() || !podeGuardar(user)) return;
    setAberta(true);
    let erroAnterior: string | null = null;
    try { erroAnterior = sessionStorage.getItem('contas:erro'); sessionStorage.removeItem('contas:erro'); } catch { /* nada */ }
    setEstado(erroAnterior ? { tom: 'erro', texto: erroAnterior } : null);
    const lista = await recarregar();
    if (!lista.some((c) => c.userId === Number(user.id))) {
      setOcupado('guardar'); setEstado({ tom: 'info', texto: 'Confirme para guardar esta conta neste aparelho.' });
      try { await guardarContaAtual(user); setEstado(null); }
      catch (e) { setEstado({ tom: 'erro', texto: (e as Error).message }); }
      finally { setOcupado(null); await recarregar(); }
    }
  }, [user, recarregar]);
  useEffect(() => { const h = () => void abrir(); window.addEventListener(EVENTO, h); return () => window.removeEventListener(EVENTO, h); }, [abrir]);
  useEffect(() => { if (!user) setAberta(false); }, [user]);

  if (!aberta || !user) return null;
  const ordenadas = [...contas].sort((a, b) => (a.userId === atual ? -1 : b.userId === atual ? 1 : 0));
  const fechar = () => setAberta(false);

  const trocar = async (c: ContaGuardada) => {
    if (c.userId === atual || ocupado !== null) return;
    setEstado(null); setOcupado(c.userId);
    try {
      const sessao = await trocarPara(c, atual);
      if (sessao) { cliente.clear(); setAberta(false); entrarComSessao(sessao); }   // sem limpar, a tela mostraria por um instante os dados da conta anterior
    } catch (e) { setEstado({ tom: 'erro', texto: (e as Error).message }); await recarregar(); }
    finally { setOcupado(null); }
  };
  const remover = async (c: ContaGuardada) => {
    setOcupado(c.userId);
    if (c.userId === atual) {
      await sairDaContaAtual(c.userId, localStorage.getItem('token'));
      setEstado({ tom: 'ok', texto: `${c.nome} não fica mais guardada neste aparelho. A sessão aberta continua.` });
    } else {
      const r = await sairDaContaGuardada(c);
      setEstado({ tom: 'ok', texto: r.revogadaNoServidor ? `${c.nome} não fica mais guardada neste aparelho.` : `${c.nome} não fica mais guardada neste aparelho. O acesso guardado vence em 90 dias, ou pode ser revogado em Minha conta, dentro daquela conta.` });
    }
    setOcupado(null); await recarregar();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40" onClick={fechar} data-testid="folha-de-contas">
      <div className="w-full max-w-md rounded-t-2xl bg-white pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Trocar de conta">
        <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-300" aria-hidden />
        <div className="flex items-center justify-between px-4 pt-3">
          <h2 className="text-base font-semibold text-tinta">Trocar de conta</h2>
          <button type="button" onClick={fechar} className="min-h-[44px] px-2 text-sm text-gray-600">Fechar</button>
        </div>
        {estado && (
          <p role={estado.tom === 'erro' ? 'alert' : 'status'} data-testid="estado-da-folha"
            className={`mx-4 mt-1 rounded-md px-3 py-2 text-sm ${estado.tom === 'erro' ? 'bg-red-50 text-red-800' : estado.tom === 'ok' ? 'bg-green-50 text-green-800' : 'bg-marca-50 text-marca-800'}`}>{estado.texto}</p>
        )}
        <ul className="mt-2 max-h-[55vh] overflow-y-auto">
          {ordenadas.map((c) => {
            const eAtual = c.userId === atual;
            return (
              <li key={c.userId} data-testid={`conta-${c.userId}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 pb-1">
                  <button type="button" disabled={ocupado !== null && !eAtual} data-testid={`folha-conta-${c.userId}`}
                    onClick={() => void trocar(c)}
                    className="flex min-h-[56px] min-w-0 flex-1 items-center gap-3 py-2 text-left select-none">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-marca-100 text-sm font-semibold text-marca-800" aria-hidden>{c.nome.trim().charAt(0).toUpperCase()}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">{c.nome}</span>
                      <span className="block truncate text-xs text-gray-500">{PAPEL[c.papel] ?? c.papel} · {c.username}</span>
                    </span>
                    {eAtual && <CheckIcon className="h-5 w-5 flex-shrink-0 text-marca-600" aria-label="conta atual" data-testid="conta-atual" />}
                    {ocupado === c.userId && <span className="text-xs text-gray-500">{eAtual ? '' : 'Entrando…'}</span>}
                  </button>
                  <MenuDaConta userId={c.userId} nome={c.nome} onde="neste aparelho" desabilitado={ocupado !== null} onEsquecer={() => remover(c)} explicacao={'A conta continua existindo; para voltar a ela aqui, será preciso a senha.'} />
                </div>
              </li>
            );
          })}
          {ocupado === 'guardar' && contas.every((c) => c.userId !== atual) && (
            <li className="px-4 py-3 text-sm text-gray-500">Guardando esta conta…</li>
          )}
        </ul>
        <div className="mt-1 border-t border-gray-100 px-2 pt-1">
          {contas.length < MAXIMO_DE_CONTAS ? (
            <button type="button" disabled={ocupado !== null} onClick={() => { setAberta(false); sairParaAdicionarConta(); }}
              className="flex min-h-[52px] w-full items-center gap-3 rounded-md px-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50" data-testid="folha-adicionar">
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300"><PlusIcon className="h-5 w-5" /></span>
              Adicionar conta
            </button>
          ) : <p className="px-2 py-3 text-sm text-gray-600">{FRASE_DO_LIMITE}</p>}
        </div>
      </div>
    </div>
  );
}
